import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { Modal } from '../components/Modal'
import { SortableTh } from '../components/SortableTh'
import { useTableSort } from '../hooks/useTableSort'
import {
  aplicarArredondamentoEmbalagemDraft,
  avaliarExcessoPedidoMensal,
  buildItensFromNecessidade,
  competenciaFromPeriodoInicio,
  emptyPedidoItemRow,
  fetchNecessidadePeriodoPedido,
  filtrarNecessidadePorGrupos,
  findPedidoCardapioSobreposto,
  direcaoArredondamentoDoItem,
  formatAvisoArredondamentoEmbalagem,
  formatAvisoExcessoPedidoMensal,
  formatGruposProdutoLabels,
  formatMesReferencia,
  formatPeriodoConsumo,
  GRUPOS_PEDIDO_SELECAO,
  type GrupoPedidoSelecao,
  itemArredondadoPorEmbalagem,
  mensagemCardapioPendente,
  mesReferencia,
  normalizeNecessidadeRows,
  parseMesReferencia,
  type NecessidadePedidoRow,
  type PedidoItemDraft,
  pedidoTemAlocacao,
  periodoMesInteiro,
  periodoSemanaSugerido,
  quantidadeDiverge,
  quantidadeRestante,
  resolverGruposProdutoParaSalvar,
  todosGruposPedidoSelecao,
  ultimoDiaMesRef,
  validarPeriodoMesmoMes,
  valorTotalItemPedido,
  valorTotalItens,
} from '../lib/pedidoMensal'
import type { DirecaoArredondamentoEmbalagem } from '../lib/produtoConversaoKg'
import {
  formatUnidadeExibicao,
  kgParaUnidadesComerciais,
  opcoesArredondamentoEmbalagem,
  produtosFechadosNecessarios,
} from '../lib/produtoConversaoKg'
import { resolvePrecoVigentePorProduto } from '../lib/precoVigente'
import { supabase } from '../lib/supabase'
import { downloadText, formatCurrency, formatDate, formatNumber, todayISO, toCsv } from '../lib/utils'
import { GRUPOS, STATUS_VALIDACAO_PEDIDO_LABELS, type GrupoProduto } from '../types/database'
import type {
  AlimentoMacronutriente,
  Compra,
  CompraItem,
  Contrato,
  Fornecedor,
  Preco,
  Produto,
  StatusValidacaoPedido,
} from '../types/database'

type CompraItemRow = CompraItem & {
  alimento?: { nome: string; categoria?: string; produto_id?: string | null }
}

type CompraRow = Compra & {
  fornecedor?: { nome: string }
  contrato?: { fornecedor: string; periodo_referencia: string } | null
  registrado?: { nome: string }
  itens?: CompraItemRow[]
}

type ParsedItem = {
  alimento_id: string
  quantidade: number
  quantidade_pnae: number
  valor_unitario: number
  justificativa: string | null
  lote: null
  validade: null
}

type ModalPedidoMode = 'cardapio' | 'extra'

const emptyModalForm = {
  data_compra: todayISO(),
  mes_ref_input: '',
  contrato_id: '',
  periodo_inicio: '',
  periodo_fim: '',
}

function digitsOnly(value: string | null | undefined) {
  return (value || '').replace(/\D/g, '')
}

function contratoMatchesFornecedor(contrato: Contrato, fornecedor: Fornecedor | undefined) {
  if (!fornecedor) return true
  if (contrato.fornecedor_id && contrato.fornecedor_id === fornecedor.id) return true
  const nomes = [fornecedor.nome, fornecedor.razao_social, fornecedor.nome_fantasia].filter(Boolean)
  if (nomes.some((nome) => contrato.fornecedor === nome)) return true
  const cnpjContrato = digitsOnly(contrato.cnpj)
  const cnpjFornecedor = digitsOnly(fornecedor.cpf_cnpj)
  if (cnpjContrato && cnpjFornecedor && cnpjContrato === cnpjFornecedor) return true
  return nomes.some(
    (nome) =>
      contrato.fornecedor.toLowerCase().includes(nome.toLowerCase()) ||
      nome.toLowerCase().includes(contrato.fornecedor.toLowerCase()),
  )
}

function fornecedorPadraoId(fornecedores: Fornecedor[], nomeFornecedor: string | null | undefined) {
  if (!nomeFornecedor) return ''
  const alvo = nomeFornecedor.toLowerCase()
  return (
    fornecedores.find(
      (f) =>
        f.nome.toLowerCase() === alvo ||
        f.razao_social.toLowerCase() === alvo ||
        (f.nome_fantasia && f.nome_fantasia.toLowerCase() === alvo),
    )?.id ?? ''
  )
}

function contratoPadraoId(
  fornecedorId: string,
  contratos: Contrato[],
  fornecedores: Fornecedor[],
  itensContrato: { contrato_id: string; alimento_id: string }[],
) {
  const fornecedor = fornecedores.find((f) => f.id === fornecedorId)
  const idsComItens = new Set(itensContrato.map((i) => i.contrato_id))
  const ok = contratos.filter((c) => {
    if (!contratoMatchesFornecedor(c, fornecedor)) return false
    if (idsComItens.size === 0) return true
    return idsComItens.has(c.id)
  })
  return ok[0]?.id ?? ''
}

function labelAlimento(categoria: string, nome: string) {
  const cat = categoria || '—'
  return `[${cat}] ${nome}`
}

function parseItensDraft(
  drafts: PedidoItemDraft[],
  alimentosDoContrato: Set<string>,
  _produtoPorAlimentoId?: Map<string, Produto | null>,
): { itens: ParsedItem[]; error?: string } {
  // Sem arredondamento automático no Pedido — embalagem fechada fica na rota (SEMED).
  void _produtoPorAlimentoId
  const parsed = drafts
    .filter((i) => i.alimento_id)
    .map((i) => {
      const rawPnae = i.quantidade_pnae.trim()
      const qPnae = rawPnae === '' ? NaN : Number(rawPnae)
      return {
        alimento_id: i.alimento_id,
        quantidade: Number(i.quantidade),
        quantidade_pnae: Number.isFinite(qPnae) ? qPnae : Number(i.quantidade),
        valor_unitario: Number(i.valor_unitario),
        justificativa: i.justificativa.trim() || null,
        lote: null as null,
        validade: null as null,
      }
    })
    .filter((i) => i.quantidade > 0 && i.valor_unitario >= 0)

  if (!parsed.length) {
    return { itens: [], error: 'Adicione ao menos um item com alimento, quantidade e valor unitário.' }
  }

  const divergentes = drafts.filter((i) => quantidadeDiverge(i) && !i.justificativa.trim())
  if (divergentes.length) {
    return { itens: [], error: 'Informe justificativa nos itens com quantidade diferente do PNAE' }
  }

  if (alimentosDoContrato.size > 0) {
    const fora = parsed.filter((i) => !alimentosDoContrato.has(i.alimento_id))
    if (fora.length) {
      return { itens: [], error: 'Há alimentos fora do contrato selecionado' }
    }
  }

  return { itens: parsed }
}

export function ComprasPage() {
  const { canWrite, canDelete, profile, canViewValores } = useAuth()
  const profileId = profile?.id
  const hoje = new Date()
  const [mesAno, setMesAno] = useState({ year: hoje.getFullYear(), month: hoje.getMonth() + 1 })
  const mesRef = mesReferencia(mesAno.year, mesAno.month)

  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([])
  const [contratos, setContratos] = useState<Contrato[]>([])
  const [alimentos, setAlimentos] = useState<AlimentoMacronutriente[]>([])
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [precos, setPrecos] = useState<Preco[]>([])
  const [itensContrato, setItensContrato] = useState<{ contrato_id: string; alimento_id: string }[]>([])
  const [fornecedorPadrao, setFornecedorPadrao] = useState('')
  const [contratoPadrao, setContratoPadrao] = useState('')
  const [pedidos, setPedidos] = useState<CompraRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<ModalPedidoMode>('cardapio')
  const [modalForm, setModalForm] = useState(emptyModalForm)
  const [modalItens, setModalItens] = useState<PedidoItemDraft[]>([])
  const [modalError, setModalError] = useState<string | null>(null)
  const [modalWarning, setModalWarning] = useState<string | null>(null)
  const [necessidadeCache, setNecessidadeCache] = useState<NecessidadePedidoRow[] | null>(null)
  const [carregandoAlimentoKey, setCarregandoAlimentoKey] = useState<string | null>(null)
  const [gerando, setGerando] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [gruposPedidoSelecionados, setGruposPedidoSelecionados] = useState<Set<GrupoPedidoSelecao>>(
    () => todosGruposPedidoSelecao(),
  )

  const writable = canWrite('compras')
  const tableSort = useTableSort<CompraRow>('data', 'desc')

  const alimentoProdutoIds = useMemo(
    () => new Map(alimentos.map((a) => [a.id, a.produto_id])),
    [alimentos],
  )

  const produtoById = useMemo(() => new Map(produtos.map((p) => [p.id, p])), [produtos])

  const produtoPorAlimentoId = useMemo(() => {
    const map = new Map<string, Produto | null>()
    for (const a of alimentos) {
      map.set(a.id, a.produto_id ? produtoById.get(a.produto_id) ?? null : null)
    }
    return map
  }, [alimentos, produtoById])

  const grupoPorAlimentoId = useMemo(() => {
    const map = new Map<string, GrupoProduto | null | undefined>()
    for (const [id, p] of produtoPorAlimentoId) {
      map.set(id, p?.grupo ?? null)
    }
    return map
  }, [produtoPorAlimentoId])

  const produtoDoItem = useCallback(
    (item: CompraItemRow) => {
      const produtoId =
        item.alimento?.produto_id ?? alimentoProdutoIds.get(item.alimento_id) ?? null
      return produtoId ? produtoById.get(produtoId) : undefined
    },
    [alimentoProdutoIds, produtoById],
  )

  const recalcularContratos = useCallback(async (...ids: (string | null | undefined)[]) => {
    const unicos = [...new Set(ids.filter(Boolean) as string[])]
    await Promise.all(
      unicos.map((id) => supabase.rpc('recalcular_entregue_contrato', { p_contrato_id: id })),
    )
  }, [])

  const fetchNecessidade = useCallback(async (ano: number, mes: number) => {
    const { data, error: err } = await supabase.rpc('necessidade_pedido_mensal', {
      p_ano: ano,
      p_mes: mes,
      p_grupo: null,
    })
    if (err) throw new Error(err.message)
    return normalizeNecessidadeRows(data || [])
  }, [])

  const mensagemSemCardapioPeriodo = useCallback(async (ano: number, mes: number) => {
    const { data, error: err } = await supabase.rpc('cardapio_pendente_pedido_mensal', {
      p_ano: ano,
      p_mes: mes,
    })
    if (err) {
      return 'Não há cardápio cadastrado para este período. Cadastre o cardápio correspondente ao calendário letivo do mês.'
    }
    return mensagemCardapioPendente(
      ((data as { trimestre: number; semana: number }[]) || []).map((r) => ({
        trimestre: Number(r.trimestre),
        semana: Number(r.semana),
      })),
    )
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const [c, f, ct, al, pr, p, ic, cfgRes] = await Promise.all([
      supabase
        .from('compras')
        .select(
          '*, fornecedor:fornecedores(nome), contrato:contratos(fornecedor, periodo_referencia), registrado:usuarios!compras_registrado_por_fkey(nome), itens:compra_itens(*, alimento:alimentos_macronutrientes(nome, categoria, produto_id))',
        )
        .eq('mes_referencia', mesRef)
        .order('numero', { ascending: true }),
      supabase.from('fornecedores').select('*').order('razao_social'),
      supabase.from('contratos').select('*').order('periodo_referencia', { ascending: false }),
      supabase.from('alimentos_macronutrientes').select('*').order('nome'),
      supabase.from('produtos').select('*').order('nome'),
      supabase.from('precos').select('*'),
      supabase.from('itens_contrato').select('contrato_id, alimento_id'),
      supabase.from('configuracoes').select('fornecedor_atual').eq('id', 1).maybeSingle(),
    ])

    if (c.error || f.error || ct.error || al.error || pr.error || p.error || ic.error) {
      setError(
        c.error?.message ||
          f.error?.message ||
          ct.error?.message ||
          al.error?.message ||
          pr.error?.message ||
          p.error?.message ||
          ic.error?.message ||
          'Erro',
      )
      setLoading(false)
      return
    }

    const fList = (f.data as Fornecedor[]) || []
    const cList = (ct.data as Contrato[]) || []
    const pList = (p.data as Preco[]) || []
    const icList = (ic.data as { contrato_id: string; alimento_id: string }[]) || []
    const fId = fornecedorPadraoId(fList, cfgRes.data?.fornecedor_atual)
    const cId = contratoPadraoId(fId, cList, fList, icList)

    setFornecedores(fList)
    setContratos(cList)
    setAlimentos((al.data as AlimentoMacronutriente[]) || [])
    setProdutos((pr.data as Produto[]) || [])
    setPrecos(pList)
    setItensContrato(icList)
    setFornecedorPadrao(fId)
    setContratoPadrao(cId)
    setPedidos((c.data as CompraRow[]) || [])

    if (writable && (!fId || !cId)) {
      setError('Configure fornecedor e contrato PNAE em Configurações antes de gerar pedidos.')
    }

    setLoading(false)
  }, [mesRef, writable])

  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    document.title = 'Pedido · ISEN EDUCAE'
    setExpandedId(null)
    void loadRef.current()
  }, [mesRef, profileId, writable])

  const contratosDoFornecedor = useMemo(() => {
    const fornecedor = fornecedores.find((f) => f.id === fornecedorPadrao)
    const idsComItens = new Set(itensContrato.map((i) => i.contrato_id))
    return contratos.filter((c) => {
      if (!contratoMatchesFornecedor(c, fornecedor)) return false
      if (idsComItens.size === 0) return true
      return idsComItens.has(c.id)
    })
  }, [contratos, fornecedores, fornecedorPadrao, itensContrato])

  const alimentosDoContratoModal = useMemo(() => {
    if (!modalForm.contrato_id) return new Set<string>()
    return new Set(
      itensContrato.filter((i) => i.contrato_id === modalForm.contrato_id).map((i) => i.alimento_id),
    )
  }, [modalForm.contrato_id, itensContrato])

  const alimentosNoCardapioIds = useMemo(
    () => new Set((necessidadeCache ?? []).map((r) => r.alimento_id)),
    [necessidadeCache],
  )

  const alimentosContratoOrdenados = useMemo(() => {
    if (!alimentosNoCardapioIds.size) return []
    const list = alimentos.filter((a) => {
      if (!alimentosNoCardapioIds.has(a.id)) return false
      if (alimentosDoContratoModal.size === 0) return true
      return alimentosDoContratoModal.has(a.id)
    })
    return [...list].sort((a, b) => {
      const cmpCat = (a.categoria || '').localeCompare(b.categoria || '', 'pt-BR')
      if (cmpCat !== 0) return cmpCat
      return a.nome.localeCompare(b.nome, 'pt-BR')
    })
  }, [alimentos, alimentosDoContratoModal, alimentosNoCardapioIds])

  const opcoesAlimentoPedido = useCallback(
    (alimentoIdAtual: string) => {
      const ids = new Set(alimentosContratoOrdenados.map((a) => a.id))
      const atual = alimentoIdAtual ? alimentos.find((a) => a.id === alimentoIdAtual) : undefined
      if (atual && !ids.has(atual.id)) return [...alimentosContratoOrdenados, atual]
      return alimentosContratoOrdenados
    },
    [alimentos, alimentosContratoOrdenados],
  )

  const pedidosSorted = useMemo(() => {
    const accessors: Record<string, (c: CompraRow) => string | number> = {
      codigo: (c) => c.codigo || '',
      data: (c) => c.data_compra,
      usuario: (c) => c.registrado?.nome || '',
      mes: (c) => c.mes_referencia || '',
      itens: (c) => c.itens?.length || 0,
      valor: (c) =>
        valorTotalItens(c.itens || [], produtoPorAlimentoId, (i) => i.alimento_id),
    }
    return tableSort.sortRows(pedidos, accessors)
  }, [pedidos, produtoPorAlimentoId, tableSort])

  const avisoArredondamentoModal = useMemo(() => {
    const itens = modalItens
      .filter((i) => i.alimento_id)
      .filter((i) => {
        const produto = produtoPorAlimentoId.get(i.alimento_id)
        const ref = Number(i.quantidade_pnae)
        const op = Number.isFinite(ref) ? opcoesArredondamentoEmbalagem(ref, produto) : null
        return (
          op?.precisaEscolha === true ||
          itemArredondadoPorEmbalagem(ref, Number(i.quantidade), produto, i.justificativa)
        )
      })
      .map((i) => {
        const produto = produtoPorAlimentoId.get(i.alimento_id)
        const ref = Number(i.quantidade_pnae) || 0
        const pedida = Number(i.quantidade) || 0
        return {
          nome: i.alimento_nome || i.alimento_id,
          referenciaKg: ref,
          pedidaKg: pedida,
          direcao: direcaoArredondamentoDoItem(
            ref,
            pedida,
            produto,
            i.justificativa,
            i.embalagem_direcao,
          ),
        }
      })
    return formatAvisoArredondamentoEmbalagem(itens)
  }, [modalItens, produtoPorAlimentoId])

  function aplicarDirecaoEmbalagem(key: string, direcao: DirecaoArredondamentoEmbalagem) {
    setModalItens((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r
        const produto = produtoPorAlimentoId.get(r.alimento_id)
        return aplicarArredondamentoEmbalagemDraft(r, produto, direcao)
      }),
    )
    clearModalFeedback()
  }

  function clearModalFeedback() {
    setModalError(null)
    setModalWarning(null)
  }

  function labelGruposConflito(grupos: GrupoProduto[]) {
    const map = new Map(GRUPOS.map((g) => [g.value, g.label]))
    return grupos.map((g) => map.get(g) || g).join(', ')
  }

  async function atualizarAvisoExcessoMensal(
    ano: number,
    mes: number,
    rascunho: PedidoItemDraft[],
  ) {
    try {
      const necessidade = await fetchNecessidade(ano, mes)
      const mesComp = mesReferencia(ano, mes)
      const pedidosItens = pedidos
        .filter((p) => (p.mes_referencia || '').slice(0, 10) === mesComp)
        .flatMap((p) =>
          (p.itens || []).map((i) => ({
            alimento_id: i.alimento_id,
            quantidade: Number(i.quantidade) || 0,
          })),
        )
      const rascunhoItens = rascunho
        .filter((i) => i.alimento_id)
        .map((i) => ({
          alimento_id: i.alimento_id,
          quantidade: Number(i.quantidade) || 0,
        }))
      const aviso = formatAvisoExcessoPedidoMensal(
        avaliarExcessoPedidoMensal({
          necessidadeMes: necessidade.map((n) => ({
            alimento_id: n.alimento_id,
            alimento: n.alimento,
            quantidade_pnae: n.quantidade_pnae,
          })),
          pedidosItens,
          rascunhoItens,
        }),
      )
      setModalWarning(aviso)
    } catch {
      // Aviso é opcional; falha de RPC não impede o fluxo
    }
  }

  function openNovoPedidoCardapio() {
    if (!fornecedorPadrao || !contratoPadrao) {
      setError('Configure fornecedor e contrato PNAE em Configurações antes de gerar pedidos.')
      return
    }
    const { inicio, fim } = periodoSemanaSugerido(mesRef)
    setModalMode('cardapio')
    setModalForm({
      data_compra: todayISO(),
      mes_ref_input: '',
      contrato_id: contratoPadrao || contratosDoFornecedor[0]?.id || '',
      periodo_inicio: inicio,
      periodo_fim: fim,
    })
    setModalItens([emptyPedidoItemRow()])
    setNecessidadeCache(null)
    setGruposPedidoSelecionados(todosGruposPedidoSelecao())
    setModalOpen(true)
    clearModalFeedback()
  }

  function openPedidoExtra() {
    const mesInput = mesRef.slice(0, 7)
    setModalMode('extra')
    setModalForm({
      data_compra: todayISO(),
      mes_ref_input: mesInput,
      contrato_id: contratoPadrao || contratosDoFornecedor[0]?.id || '',
      periodo_inicio: '',
      periodo_fim: '',
    })
    setModalItens([emptyPedidoItemRow()])
    setNecessidadeCache(null)
    setGruposPedidoSelecionados(todosGruposPedidoSelecao())
    setModalOpen(true)
    clearModalFeedback()
    const [y, m] = mesInput.split('-').map(Number)
    if (y && m) void cacheNecessidadeMes(y, m).catch(() => undefined)
  }

  function closeModal() {
    setModalOpen(false)
    setModalItens([])
    setNecessidadeCache(null)
    setGruposPedidoSelecionados(todosGruposPedidoSelecao())
    clearModalFeedback()
  }

  function toggleGrupoPedido(value: GrupoPedidoSelecao) {
    setGruposPedidoSelecionados((prev) => {
      const next = new Set(prev)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
  }

  function patchModalForm(patch: Partial<typeof emptyModalForm>) {
    setModalForm((prev) => ({ ...prev, ...patch }))
    clearModalFeedback()
  }

  async function cacheNecessidadeMes(ano: number, mes: number) {
    const rows = await fetchNecessidade(ano, mes)
    setNecessidadeCache(rows)
    return rows
  }

  async function gerarItensModal() {
    if (!modalForm.contrato_id) {
      setModalError('Selecione o contrato PNAE.')
      return
    }
    if (!gruposPedidoSelecionados.size) {
      setModalError('Selecione ao menos um grupo deste pedido.')
      return
    }
    setGerando(true)
    clearModalFeedback()
    try {
      let rows: NecessidadePedidoRow[]
      let refPreco: string
      let pendenciaAno: number
      let pendenciaMes: number

      if (modalMode === 'cardapio') {
        const val = validarPeriodoMesmoMes(modalForm.periodo_inicio, modalForm.periodo_fim)
        if (!val.ok) {
          setModalError(val.error)
          return
        }
        rows = await fetchNecessidadePeriodoPedido(
          modalForm.periodo_inicio,
          modalForm.periodo_fim,
        )
        setNecessidadeCache(rows)
        const mesComp = competenciaFromPeriodoInicio(modalForm.periodo_inicio)
        refPreco = ultimoDiaMesRef(mesComp)
        pendenciaAno = parseMesReferencia(mesComp).ano
        pendenciaMes = parseMesReferencia(mesComp).mes
      } else {
        if (!modalForm.mes_ref_input) {
          setModalError('Informe o mês de referência.')
          return
        }
        const [y, m] = modalForm.mes_ref_input.split('-').map(Number)
        if (!y || !m) return
        const ref = mesReferencia(y, m)
        rows = await cacheNecessidadeMes(y, m)
        refPreco = ultimoDiaMesRef(ref)
        pendenciaAno = y
        pendenciaMes = m
      }

      if (!rows.length) {
        setModalError(await mensagemSemCardapioPeriodo(pendenciaAno, pendenciaMes))
        return
      }
      const allowed =
        alimentosDoContratoModal.size === 0
          ? rows
          : rows.filter((r) => alimentosDoContratoModal.has(r.alimento_id))
      if (!allowed.length) {
        setModalError(
          modalMode === 'cardapio'
            ? 'Nenhum item do contrato no consumo previsto deste período.'
            : 'Nenhum item do contrato selecionado no consumo previsto deste mês.',
        )
        return
      }
      const filtrados = filtrarNecessidadePorGrupos(
        allowed,
        gruposPedidoSelecionados,
        grupoPorAlimentoId,
      )
      if (!filtrados.length) {
        setModalError('Nenhum item dos grupos selecionados no consumo previsto.')
        return
      }
      const built = buildItensFromNecessidade(
        filtrados,
        precos,
        refPreco,
        alimentoProdutoIds,
        produtoPorAlimentoId,
      )
      setModalItens(built)
      await atualizarAvisoExcessoMensal(pendenciaAno, pendenciaMes, built)
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Erro ao gerar itens do cardápio')
    } finally {
      setGerando(false)
    }
  }

  function gruposProdutoParaSalvar(): GrupoProduto[] | null {
    const ids = modalItens.map((i) => i.alimento_id).filter(Boolean)
    const grupos = resolverGruposProdutoParaSalvar(
      gruposPedidoSelecionados,
      ids,
      grupoPorAlimentoId,
    )
    return grupos.length ? grupos : null
  }

  function addModalItem() {
    setModalItens((prev) => [...prev, emptyPedidoItemRow()])
    clearModalFeedback()
  }

  function removeModalItem(key: string) {
    setModalItens((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)))
    clearModalFeedback()
  }

  function updateModalItem(key: string, patch: Partial<PedidoItemDraft>) {
    setModalItens((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r
        const next = { ...r, ...patch }
        if ('quantidade' in patch && !quantidadeDiverge(next)) {
          next.justificativa = ''
          next.embalagem_direcao = ''
        }
        if ('quantidade' in patch && !('embalagem_direcao' in patch)) {
          next.embalagem_direcao = ''
        }
        return next
      }),
    )
    clearModalFeedback()
  }

  async function onModalAlimentoChange(key: string, alimentoId: string) {
    if (!alimentoId) {
      updateModalItem(key, {
        alimento_id: '',
        alimento_nome: '',
        alimento_grupo: '',
        unidade: 'kg',
        quantidade_pnae: '',
        quantidade: '',
        valor_unitario: '',
        justificativa: '',
      })
      return
    }

    if (modalMode === 'cardapio') {
      const val = validarPeriodoMesmoMes(modalForm.periodo_inicio, modalForm.periodo_fim)
      if (!val.ok) {
        setModalError(val.error)
        return
      }
    } else if (!modalForm.mes_ref_input) {
      setModalError('Informe o mês de competência antes de escolher o alimento.')
      return
    }

    const alimento = alimentos.find((a) => a.id === alimentoId)
    if (!alimento) return

    setCarregandoAlimentoKey(key)
    clearModalFeedback()

    try {
      let cache: NecessidadePedidoRow[]
      let refPreco: string

      if (modalMode === 'cardapio') {
        cache =
          necessidadeCache ??
          (await fetchNecessidadePeriodoPedido(modalForm.periodo_inicio, modalForm.periodo_fim))
        refPreco = ultimoDiaMesRef(competenciaFromPeriodoInicio(modalForm.periodo_inicio))
      } else {
        const [y, m] = modalForm.mes_ref_input.split('-').map(Number)
        if (!y || !m) return
        cache = necessidadeCache ?? (await cacheNecessidadeMes(y, m))
        refPreco = ultimoDiaMesRef(`${modalForm.mes_ref_input}-01`)
      }

      const row = cache.find((r) => r.alimento_id === alimentoId)
      const preco = resolvePrecoVigentePorProduto(precos, alimento.produto_id, refPreco)
      const produto = produtoPorAlimentoId.get(alimentoId)
      const draft: PedidoItemDraft = {
        key,
        alimento_id: alimentoId,
        alimento_nome: alimento.nome,
        alimento_grupo: alimento.categoria,
        unidade: 'kg',
        quantidade_pnae: row ? String(row.quantidade_pnae) : '',
        quantidade: row ? String(row.quantidade_cardapio) : '',
        valor_unitario: preco ? String(preco.valor_unitario) : '',
        justificativa: '',
        embalagem_direcao: '',
      }
      void produto

      updateModalItem(key, {
        alimento_id: draft.alimento_id,
        alimento_nome: draft.alimento_nome,
        alimento_grupo: draft.alimento_grupo,
        unidade: draft.unidade,
        quantidade_pnae: draft.quantidade_pnae,
        quantidade: draft.quantidade,
        valor_unitario: draft.valor_unitario,
        justificativa: draft.justificativa,
        embalagem_direcao: draft.embalagem_direcao,
      })

      if (!row) {
        setModalError(
          modalMode === 'cardapio'
            ? 'Este alimento não tem consumo previsto no período selecionado.'
            : 'Este alimento não tem consumo previsto no mês selecionado.',
        )
      }
    } catch (e) {
      setModalError(
        e instanceof Error
          ? e.message
          : 'Erro ao buscar quantidades PNAE. Verifique se a migration local foi aplicada (npx supabase migration up --local).',
      )
    }

    setCarregandoAlimentoKey(null)
  }

  async function savePedidoCardapio() {
    if (!writable || !profile || !fornecedorPadrao) return
    if (!modalForm.contrato_id) {
      setModalError('Selecione o contrato PNAE')
      return
    }
    if (!gruposPedidoSelecionados.size) {
      setModalError('Selecione ao menos um grupo deste pedido.')
      return
    }
    const val = validarPeriodoMesmoMes(modalForm.periodo_inicio, modalForm.periodo_fim)
    if (!val.ok) {
      setModalError(val.error)
      return
    }
    const gruposSalvar = gruposProdutoParaSalvar()
    const conflito = findPedidoCardapioSobreposto(
      pedidos,
      modalForm.periodo_inicio,
      modalForm.periodo_fim,
      gruposSalvar,
    )
    if (conflito) {
      setModalError(
        `Grupo(s) ${labelGruposConflito(conflito.gruposConflito)} já cobertos pelo pedido ${conflito.codigo}. Ajuste os grupos, o período ou use Pedido extra.`,
      )
      return
    }
    const { itens: parsed, error: parseErr } = parseItensDraft(
      modalItens,
      alimentosDoContratoModal,
      produtoPorAlimentoId,
    )
    if (parseErr) {
      setModalError(parseErr)
      return
    }

    setSalvando(true)
    setModalError(null)

    const mesComp = competenciaFromPeriodoInicio(modalForm.periodo_inicio)
    const { ano, mes } = parseMesReferencia(mesComp)
    await atualizarAvisoExcessoMensal(ano, mes, modalItens)

    const { error: errVal } = await supabase.rpc('assert_compra_periodo_valido', {
      p_inicio: modalForm.periodo_inicio,
      p_fim: modalForm.periodo_fim,
      p_avulso: false,
      p_excluir_id: null,
      p_grupos: gruposSalvar,
    })
    if (errVal) {
      setModalError(errVal.message)
      setSalvando(false)
      return
    }

    const valorPlanejado = parsed.reduce((s, i) => s + i.quantidade * i.valor_unitario, 0)

    const { data: created, error: errIns } = await supabase
      .from('compras')
      .insert({
        fornecedor_id: fornecedorPadrao,
        contrato_id: modalForm.contrato_id,
        data_compra: modalForm.data_compra,
        mes_referencia: mesComp,
        periodo_inicio: modalForm.periodo_inicio,
        periodo_fim: modalForm.periodo_fim,
        modalidade_grupo: null,
        grupos_produto: gruposSalvar,
        avulso: false,
        nota_fiscal: null,
        registrado_por: profile.id,
        status_validacao: 'rascunho',
        valor_planejado: valorPlanejado,
        valor_previsto: null,
      })
      .select('id')
      .single()

    if (errIns || !created) {
      setModalError(errIns?.message || 'Erro ao salvar pedido')
      setSalvando(false)
      return
    }

    const { error: errItens } = await supabase.from('compra_itens').insert(
      parsed.map((i) => ({ ...i, compra_id: created.id })),
    )
    if (errItens) {
      setModalError(errItens.message)
      setSalvando(false)
      return
    }

    await recalcularContratos(modalForm.contrato_id)
    setSalvando(false)
    closeModal()
    load()
  }

  async function saveAvulso() {
    if (!writable || !profile || !fornecedorPadrao) return
    if (!modalForm.contrato_id) {
      setModalError('Selecione o contrato PNAE')
      return
    }
    if (!gruposPedidoSelecionados.size) {
      setModalError('Selecione ao menos um grupo deste pedido.')
      return
    }
    const { itens: parsed, error: parseErr } = parseItensDraft(
      modalItens,
      alimentosDoContratoModal,
      produtoPorAlimentoId,
    )
    if (parseErr) {
      setModalError(parseErr)
      return
    }

    const mesRefPedido = modalForm.mes_ref_input
      ? `${modalForm.mes_ref_input}-01`
      : mesRef
    const { inicio: periodoInicio, fim: periodoFim } = periodoMesInteiro(mesRefPedido)

    setSalvando(true)
    setModalError(null)
    const { ano, mes } = parseMesReferencia(mesRefPedido)
    await atualizarAvisoExcessoMensal(ano, mes, modalItens)

    const valorPlanejado = parsed.reduce((s, i) => s + i.quantidade * i.valor_unitario, 0)

    const { data: created, error: errIns } = await supabase
      .from('compras')
      .insert({
        fornecedor_id: fornecedorPadrao,
        contrato_id: modalForm.contrato_id,
        data_compra: modalForm.data_compra,
        mes_referencia: mesRefPedido,
        periodo_inicio: periodoInicio,
        periodo_fim: periodoFim,
        modalidade_grupo: null,
        grupos_produto: gruposProdutoParaSalvar(),
        avulso: true,
        nota_fiscal: null,
        registrado_por: profile.id,
        status_validacao: 'rascunho',
        valor_planejado: valorPlanejado,
        valor_previsto: null,
      })
      .select('id')
      .single()

    if (errIns || !created) {
      setModalError(errIns?.message || 'Erro ao salvar pedido')
      setSalvando(false)
      return
    }

    const { error: errItens } = await supabase.from('compra_itens').insert(
      parsed.map((i) => ({ ...i, compra_id: created.id })),
    )
    if (errItens) {
      setModalError(errItens.message)
      setSalvando(false)
      return
    }

    await recalcularContratos(modalForm.contrato_id)
    setSalvando(false)
    closeModal()
    load()
  }

  async function removePedido(id: string) {
    const pedido = pedidos.find((c) => c.id === id)
    if (pedidoTemAlocacao(pedido?.itens)) {
      setError(`Pedido ${pedido?.codigo || ''} possui quantidade alocada em rotas. Remova os planejamentos vinculados antes de excluir.`)
      return
    }
    if (!confirm(`Excluir o pedido ${pedido?.codigo || ''} e todos os itens?`)) return
    const { error: err } = await supabase.from('compras').delete().eq('id', id)
    if (err) setError(err.message)
    else {
      await recalcularContratos(pedido?.contrato_id)
      if (expandedId === id) setExpandedId(null)
      load()
    }
  }

  function exportCsv() {
    downloadText(
      `pedidos-${mesRef.slice(0, 7)}.csv`,
      toCsv(
        pedidosSorted.map((c) => {
          const row: Record<string, string | number> = {
            Código: c.codigo,
            Data: c.data_compra,
            Usuário: c.registrado?.nome || '',
            Competência: c.mes_referencia ? formatMesReferencia(c.mes_referencia) : '',
            'Período consumo':
              c.periodo_inicio && c.periodo_fim
                ? formatPeriodoConsumo(c.periodo_inicio, c.periodo_fim)
                : '',
            Grupos: formatGruposProdutoLabels(c.grupos_produto),
            Itens: c.itens?.length || 0,
            Extra: c.avulso ? 'Sim' : 'Não',
          }
          if (canViewValores) {
            row['Valor total'] = valorTotalItens(
              c.itens || [],
              produtoPorAlimentoId,
              (i) => i.alimento_id,
            )
          }
          return row
        }),
      ),
    )
  }

  // base 8 + status + (valor planejado/previsto: 2 se canViewValores, senão valor único legado 1) + actions
  const pedidosColCount =
    9 + (canViewValores ? 2 : 0) + (writable ? 1 : 0)

  const podeValidarIsen =
    profile?.perfil === 'admin' || profile?.perfil === 'fiscal'

  async function validarPedidoIsen(compraId: string) {
    if (!podeValidarIsen) return
    setError(null)
    const { error: err } = await supabase.rpc('validar_pedido_isen', { p_compra_id: compraId })
    if (err) {
      setError(err.message)
      return
    }
    await load()
  }

  function labelStatusPedido(status: StatusValidacaoPedido | undefined) {
    return STATUS_VALIDACAO_PEDIDO_LABELS[status || 'rascunho']
  }

  const competenciaModalCardapio = useMemo(() => {
    if (!modalForm.periodo_inicio) return '—'
    return formatMesReferencia(competenciaFromPeriodoInicio(modalForm.periodo_inicio))
  }, [modalForm.periodo_inicio])
  const detailItemColCount = canViewValores ? 12 : 10

  const mesTitulo = new Date(mesAno.year, mesAno.month - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  })

  if (loading) {
    return <div className="loading">Carregando pedidos…</div>
  }

  return (
    <div>
      <div className="page-header">
        <h1>Pedido</h1>
        <div className="page-actions">
          <label className="field" style={{ margin: 0 }}>
            <span className="kpi-sub" style={{ marginRight: 8 }}>
              Mês de competência
            </span>
            <input
              type="month"
              value={mesRef.slice(0, 7)}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-').map(Number)
                if (y && m) setMesAno({ year: y, month: m })
              }}
            />
          </label>
          <button type="button" className="btn btn-secondary" onClick={exportCsv}>
            Exportar CSV
          </button>
          {writable && (
            <>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!fornecedorPadrao || !contratoPadrao}
                onClick={openNovoPedidoCardapio}
              >
                Novo pedido do cardápio
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={openPedidoExtra}
                title="Compra fora do pedido semanal do cardápio (não entra na regra de sobreposição de período)"
              >
                Pedido extra
              </button>
            </>
          )}
        </div>
      </div>

      {error && !modalOpen && <div className="alert alert-error">{error}</div>}

      <p className="kpi-sub" style={{ marginBottom: 12 }}>
        Competência: <strong style={{ textTransform: 'capitalize' }}>{mesTitulo}</strong> — cada
        pedido recebe um código <strong>número.mês.ano</strong> (ex.: 1.10.2026). Quantidades do
        cardápio (sem arredondar aqui); a Nutricionista SEMED arredonda por escola na rota; a
        Nutricionista ISEN valida o valor previsto e libera para Entregas.
      </p>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th className="col-compact" />
              <SortableTh
                label="Código do pedido"
                sortKey="codigo"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Data"
                sortKey="data"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="date"
              />
              <SortableTh
                label="Usuário"
                sortKey="usuario"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="name"
              />
              <SortableTh
                label="Competência"
                sortKey="mes"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="text"
              />
              <th className="col-text">Período consumo</th>
              <th className="col-text">Grupos</th>
              <th className="col-text">Status</th>
              <SortableTh
                label="Qtd itens"
                sortKey="itens"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="num"
              />
              {canViewValores && (
                <>
                  <th className="col-num">Planejado (R$)</th>
                  <th className="col-num">Previsto (R$)</th>
                </>
              )}
              {writable && <th className="col-actions" />}
            </tr>
          </thead>
          <tbody>
            {pedidosSorted.map((c) => {
              const valorTotal = valorTotalItens(
                c.itens || [],
                produtoPorAlimentoId,
                (i) => i.alimento_id,
              )
              const valorPlanejado =
                c.valor_planejado != null ? Number(c.valor_planejado) : valorTotal
              const valorPrevisto =
                c.valor_previsto != null ? Number(c.valor_previsto) : null
              const status = (c.status_validacao || 'rascunho') as StatusValidacaoPedido
              const expanded = expandedId === c.id
              return (
                <Fragment key={c.id}>
                  <tr
                    className={expanded ? 'row-active' : undefined}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpandedId(expanded ? null : c.id)}
                  >
                    <td className="col-compact">{expanded ? '▼' : '▶'}</td>
                    <td className="col-text">
                      <strong>{c.codigo || '—'}</strong>
                      {c.avulso ? (
                        <span
                          className="kpi-sub"
                          title="Pedido extra: fora do planejamento semanal do cardápio; não bloqueia outros pedidos no mesmo mês"
                        >
                          {' '}
                          · extra
                        </span>
                      ) : null}
                    </td>
                    <td className="col-date">{formatDate(c.data_compra)}</td>
                    <td className="col-name">{c.registrado?.nome || '—'}</td>
                    <td className="col-text">
                      {c.mes_referencia ? formatMesReferencia(c.mes_referencia) : '—'}
                    </td>
                    <td className="col-text">
                      {c.periodo_inicio && c.periodo_fim
                        ? formatPeriodoConsumo(c.periodo_inicio, c.periodo_fim)
                        : '—'}
                    </td>
                    <td className="col-text">
                      <span className="kpi-sub">{formatGruposProdutoLabels(c.grupos_produto)}</span>
                    </td>
                    <td className="col-text">
                      <span
                        className={
                          status === 'validado_isen'
                            ? 'badge badge-ok'
                            : status === 'aguardando_isen'
                              ? 'badge badge-atencao'
                              : 'badge badge-neutral'
                        }
                      >
                        {labelStatusPedido(status)}
                      </span>
                    </td>
                    <td className="col-num">{c.itens?.length || 0}</td>
                    {canViewValores && (
                      <>
                        <td className="col-num">{formatCurrency(valorPlanejado)}</td>
                        <td className="col-num">
                          {valorPrevisto != null ? formatCurrency(valorPrevisto) : '—'}
                        </td>
                      </>
                    )}
                    {writable && (
                      <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                        {podeValidarIsen && status === 'aguardando_isen' && (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => void validarPedidoIsen(c.id)}
                            >
                              Validar
                            </button>
                          )}
                        {canDelete('compras') && (
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void removePedido(c.id)}>
                            Excluir
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  {expanded && (
                    <tr key={`${c.id}-detail`}>
                      <td colSpan={pedidosColCount} style={{ padding: 0 }}>
                        <div className="table-wrap" style={{ margin: '8px 12px 12px' }}>
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Macronutriente</th>
                                <th>Produto</th>
                                <th className="col-num">Qtd prod. fechado</th>
                                <th className="col-num">Ficha técnica (kg)</th>
                                <th className="col-num">Qtd pedida (kg)</th>
                                <th className="col-num">Ficha técnica (un)</th>
                                <th className="col-num">Pedido (un)</th>
                                <th>Justificativa</th>
                                <th className="col-num">Alocada em rota</th>
                                <th className="col-num">Restante (kg)</th>
                                {canViewValores && (
                                  <>
                                    <th className="col-num">Valor un.</th>
                                    <th className="col-num">Valor total produto</th>
                                  </>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {(c.itens || []).map((item) => {
                                const categoria = item.alimento?.categoria || ''
                                const nome = item.alimento?.nome || ''
                                const produto = produtoDoItem(item)
                                const qtdFichaKg = Number(item.quantidade_pnae ?? item.quantidade)
                                const qtdPedidaKg = Number(item.quantidade)
                                const alocada = Number(item.quantidade_alocada) || 0
                                const fichaUn = produto
                                  ? kgParaUnidadesComerciais(qtdFichaKg, produto)
                                  : null
                                const pedidoUn = produto
                                  ? kgParaUnidadesComerciais(qtdPedidaKg, produto)
                                  : null
                                const prodFechado = produtosFechadosNecessarios(pedidoUn)
                                const valorTotalProduto = valorTotalItemPedido(item, produto)
                                const arredondado = itemArredondadoPorEmbalagem(
                                  qtdFichaKg,
                                  qtdPedidaKg,
                                  produto,
                                  item.justificativa,
                                )
                                const direcao = direcaoArredondamentoDoItem(
                                  qtdFichaKg,
                                  qtdPedidaKg,
                                  produto,
                                  item.justificativa,
                                )
                                return (
                                  <tr key={item.id}>
                                    <td>
                                      {labelAlimento(categoria, nome)}
                                      {arredondado && (
                                        <span
                                          className="badge badge-atencao"
                                          style={{ marginLeft: 6 }}
                                          title={`${formatNumber(qtdFichaKg, 3)} kg → ${formatNumber(qtdPedidaKg, 3)} kg (embalagem fechada)`}
                                        >
                                          {direcao === 'menos'
                                            ? 'Arredondado ↓'
                                            : direcao === 'mais'
                                              ? 'Arredondado ↑'
                                              : 'Arredondado'}
                                        </span>
                                      )}
                                    </td>
                                    <td>{produto?.nome || '—'}</td>
                                    <td className="col-num">
                                      {prodFechado != null ? formatNumber(prodFechado, 0) : '—'}
                                    </td>
                                    <td className="col-num">{formatNumber(qtdFichaKg, 3)}</td>
                                    <td className="col-num">{formatNumber(qtdPedidaKg, 3)}</td>
                                    <td className="col-num">
                                      {fichaUn != null ? formatNumber(fichaUn, 3) : '—'}
                                    </td>
                                    <td className="col-num">
                                      {pedidoUn != null ? formatNumber(pedidoUn, 3) : '—'}
                                    </td>
                                    <td>{item.justificativa || '—'}</td>
                                    <td className="col-num">{formatNumber(alocada, 3)}</td>
                                    <td className="col-num">{formatNumber(quantidadeRestante(item), 3)}</td>
                                    {canViewValores && (
                                      <>
                                        <td className="col-num">{formatCurrency(Number(item.valor_unitario))}</td>
                                        <td className="col-num">{formatCurrency(valorTotalProduto)}</td>
                                      </>
                                    )}
                                  </tr>
                                )
                              })}
                              {!c.itens?.length && (
                                <tr>
                                  <td colSpan={detailItemColCount} className="kpi-sub">
                                    Nenhum item neste pedido.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
            {!pedidosSorted.length && (
              <tr>
                <td colSpan={pedidosColCount} className="kpi-sub">
                  Nenhum pedido neste mês. Use «Novo pedido do cardápio» ou «Pedido extra».
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        title={modalMode === 'cardapio' ? 'Novo pedido do cardápio' : 'Pedido extra'}
        size="xl"
        onClose={closeModal}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() =>
                void (modalMode === 'cardapio' ? savePedidoCardapio() : saveAvulso())
              }
              disabled={salvando}
            >
              {salvando ? 'Salvando…' : 'Salvar pedido'}
            </button>
          </>
        }
      >
        {modalError && <div className="alert alert-error">{modalError}</div>}
        {modalWarning && <div className="alert alert-warning">{modalWarning}</div>}
        {avisoArredondamentoModal && (
          <div className="alert alert-warning" role="status">
            {avisoArredondamentoModal}
          </div>
        )}
        {modalMode === 'cardapio' && (
          <p className="kpi-sub" style={{ marginBottom: 12 }}>
            Informe <strong>Período início</strong> e <strong>Período fim</strong> do consumo (ex.:
            segunda a sexta da semana). A competência é o mês da data inicial. As quantidades vêm
            do cardápio nesse intervalo (preço PNAE: último dia do mês de competência).
          </p>
        )}
        {modalMode === 'extra' && (
          <p className="kpi-sub" style={{ marginBottom: 12 }}>
            Use <strong>Pedido extra</strong> para compras fora do pedido semanal do cardápio —
            reposição de estoque, matrícula adicional, ajuste fiscal ou itens que não entraram no
            pedido planejado. Não há bloqueio de sobreposição de período (regra C2). «Gerar do
            cardápio» usa a necessidade do <strong>mês inteiro</strong> de competência; ajuste as
            quantidades manualmente se precisar de um intervalo menor.
          </p>
        )}
        <div className="form-grid">
          <div className="field">
            <label>Data do pedido *</label>
            <input
              type="date"
              value={modalForm.data_compra}
              onChange={(e) => patchModalForm({ data_compra: e.target.value })}
            />
          </div>
          {modalMode === 'cardapio' ? (
            <>
              <div className="field">
                <label>Competência (automática)</label>
                <input type="text" readOnly value={competenciaModalCardapio} />
              </div>
              <div className="field">
                <label>Período início *</label>
                <input
                  type="date"
                  value={modalForm.periodo_inicio}
                  onChange={(e) => {
                    patchModalForm({ periodo_inicio: e.target.value })
                    setNecessidadeCache(null)
                  }}
                />
              </div>
              <div className="field">
                <label>Período fim *</label>
                <input
                  type="date"
                  value={modalForm.periodo_fim}
                  onChange={(e) => {
                    patchModalForm({ periodo_fim: e.target.value })
                    setNecessidadeCache(null)
                  }}
                />
              </div>
            </>
          ) : (
            <div className="field">
              <label>Mês de competência *</label>
              <input
                type="month"
                value={modalForm.mes_ref_input}
                onChange={(e) => {
                  const mes = e.target.value
                  patchModalForm({ mes_ref_input: mes })
                  setNecessidadeCache(null)
                  const [y, m] = mes.split('-').map(Number)
                  if (y && m) void cacheNecessidadeMes(y, m).catch(() => undefined)
                }}
              />
            </div>
          )}
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Contrato PNAE *</label>
            <select
              value={modalForm.contrato_id}
              onChange={(e) => patchModalForm({ contrato_id: e.target.value })}
            >
              <option value="">Selecione o contrato vigente</option>
              {contratosDoFornecedor.map((ct) => (
                <option key={ct.id} value={ct.id}>
                  {ct.periodo_referencia} — {ct.fornecedor}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Grupos deste pedido *</label>
            <p className="kpi-sub" style={{ marginBottom: 8 }}>
              Marque os tipos que entram nesta compra. A geração e o salvamento usam só estes grupos.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginBottom: 8 }}>
              {GRUPOS_PEDIDO_SELECAO.map((g) => (
                <label
                  key={g.value}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={gruposPedidoSelecionados.has(g.value)}
                    onChange={() => toggleGrupoPedido(g.value)}
                  />
                  {g.label}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setGruposPedidoSelecionados(todosGruposPedidoSelecao())}
              >
                Marcar todos
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setGruposPedidoSelecionados(new Set())}
              >
                Desmarcar todos
              </button>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '16px 0 8px',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <strong>Itens do pedido</strong>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => void gerarItensModal()}
              disabled={gerando}
            >
              {gerando ? 'Gerando…' : modalMode === 'cardapio' ? 'Gerar itens' : 'Gerar do cardápio'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addModalItem}>
              + Adicionar item
            </button>
          </div>
        </div>

        <div className="table-wrap">
          {!alimentosContratoOrdenados.length &&
            (modalMode === 'extra' ? modalForm.mes_ref_input : modalForm.periodo_inicio) && (
            <p className="kpi-sub" style={{ marginBottom: 8 }}>
              Nenhum alimento com consumo previsto. Aplique a ficha técnica ao calendário letivo ou use «Gerar
              itens» após configurar o período.
            </p>
          )}
          <table className="data-table">
            <thead>
              <tr>
                <th>Alimento</th>
                <th className="col-num">Qtd PNAE</th>
                <th>Embalagem</th>
                <th className="col-num">Qtd solicitada</th>
                {canViewValores && <th className="col-num">Valor un.</th>}
                <th>Justificativa</th>
                <th className="col-actions" />
              </tr>
            </thead>
            <tbody>
              {modalItens.map((row) => {
                const diverge = quantidadeDiverge(row)
                const produto = produtoPorAlimentoId.get(row.alimento_id)
                const refKg = Number(row.quantidade_pnae)
                const opcoesEmbalagem =
                  Number.isFinite(refKg) && refKg > 0
                    ? opcoesArredondamentoEmbalagem(refKg, produto)
                    : null
                const direcao = direcaoArredondamentoDoItem(
                  refKg,
                  Number(row.quantidade),
                  produto,
                  row.justificativa,
                  row.embalagem_direcao,
                )
                const unLabel = produto ? formatUnidadeExibicao(produto.unidade) : 'un'
                return (
                  <tr key={row.key} className={diverge ? 'row-warn' : undefined}>
                    <td>
                      <select
                        value={row.alimento_id}
                        onChange={(e) => void onModalAlimentoChange(row.key, e.target.value)}
                      >
                        <option value="">Selecione o alimento</option>
                        {opcoesAlimentoPedido(row.alimento_id).map((a) => (
                          <option key={a.id} value={a.id}>
                            {labelAlimento(a.categoria, a.nome)} (kg)
                          </option>
                        ))}
                      </select>
                      {direcao && (
                        <span
                          className="badge badge-atencao"
                          style={{ marginTop: 4, display: 'inline-block' }}
                          title={`${row.quantidade_pnae} kg → ${row.quantidade} kg`}
                        >
                          {direcao === 'menos' ? 'Arredondado ↓' : 'Arredondado ↑'}
                        </span>
                      )}
                    </td>
                    <td className="col-num">
                      {carregandoAlimentoKey === row.key ? (
                        <span className="kpi-sub">…</span>
                      ) : row.quantidade_pnae !== '' ? (
                        formatNumber(Number(row.quantidade_pnae), 3)
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {opcoesEmbalagem?.precisaEscolha ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 168 }}>
                          <button
                            type="button"
                            className={`btn btn-sm ${direcao === 'menos' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => aplicarDirecaoEmbalagem(row.key, 'menos')}
                            disabled={opcoesEmbalagem.menos.un <= 0}
                            title={
                              opcoesEmbalagem.menos.un <= 0
                                ? 'Menos de 1 embalagem — não há quantidade para baixo'
                                : `Para menos: ${formatNumber(opcoesEmbalagem.menos.un, 0)} ${unLabel} (${formatNumber(opcoesEmbalagem.menos.deltaKg, 3)} kg vs PNAE)`
                            }
                          >
                            Menos · {formatNumber(opcoesEmbalagem.menos.kg, 3)} kg (
                            {formatNumber(opcoesEmbalagem.menos.un, 0)} {unLabel})
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${direcao === 'mais' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => aplicarDirecaoEmbalagem(row.key, 'mais')}
                            title={`Para mais: ${formatNumber(opcoesEmbalagem.mais.un, 0)} ${unLabel} (+${formatNumber(opcoesEmbalagem.mais.deltaKg, 3)} kg vs PNAE)`}
                          >
                            Mais · {formatNumber(opcoesEmbalagem.mais.kg, 3)} kg (
                            {formatNumber(opcoesEmbalagem.mais.un, 0)} {unLabel})
                          </button>
                          <span className="kpi-sub">
                            Necessidade: {formatNumber(opcoesEmbalagem.unExata, 3)} {unLabel}
                          </span>
                        </div>
                      ) : (
                        <span className="kpi-sub">—</span>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={row.quantidade}
                        onChange={(e) => updateModalItem(row.key, { quantidade: e.target.value })}
                      />
                    </td>
                    {canViewValores && (
                      <td>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          value={row.valor_unitario}
                          onChange={(e) => updateModalItem(row.key, { valor_unitario: e.target.value })}
                        />
                      </td>
                    )}
                    <td>
                      <input
                        value={row.justificativa}
                        disabled={!diverge}
                        placeholder={diverge ? 'Obrigatória' : '—'}
                        onChange={(e) => updateModalItem(row.key, { justificativa: e.target.value })}
                      />
                    </td>
                    <td className="col-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => removeModalItem(row.key)}
                        disabled={modalItens.length <= 1}
                      >
                        Remover
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {canViewValores && (
            <div style={{ marginTop: 8, textAlign: 'right' }}>
              Total:{' '}
              {formatCurrency(
                valorTotalItens(
                  modalItens.filter((i) => i.alimento_id),
                  produtoPorAlimentoId,
                  (i) => i.alimento_id,
                ),
              )}{' '}
              (
              {formatNumber(modalItens.filter((i) => i.alimento_id).length, 0)} itens)
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}
